import { Button, Stack } from '@mui/material';
import { contentTypePluralToOneMap } from '../../types/types';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import useContentSearchParams from '../../hooks/useContentSearchParams';

interface ContentTypeLinkProps {
  active?: boolean;
  onClickCallback: () => void;
  children: React.ReactNode;
}

function ContentTypeLink({
  active,
  onClickCallback,
  children,
}: Readonly<ContentTypeLinkProps>) {
  return (
    <Button
      variant='text'
      sx={{
        whiteSpace: 'nowrap',
        lineHeight: '150%',
        minWidth: 'auto',
        paddingX: 2,
        paddingY: 0.5,
        fontSize: '0.875rem !important',
        fontWeight: 600,
        textDecoration: 'none',
        color: active ? 'primary.main' : 'text.secondary',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
      onClick={() => onClickCallback()}
    >
      {children}
    </Button>
  );
}

const contentTypesPlural = Object.keys(contentTypePluralToOneMap);

export default function ContentTypeBar() {
  const { t } = useTranslation();

  const { contentTypePlural: activeContentTypePlural, updateSearchParam } =
    useContentSearchParams();
  const selectContentType = useCallback(
    (contentTypePlural?: string) => {
      updateSearchParam('contentType', contentTypePlural);
    },
    [updateSearchParam],
  );

  return (
    <Stack
      direction='row'
      sx={{
        paddingY: 0.25,
        overflowX: 'scroll',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      <ContentTypeLink
        active={activeContentTypePlural === undefined}
        onClickCallback={() => selectContentType()}
      >
        {t('contentType.all')}
      </ContentTypeLink>
      {contentTypesPlural.map((contentType) => {
        return (
          <ContentTypeLink
            key={contentType}
            active={activeContentTypePlural === contentType}
            onClickCallback={() => selectContentType(contentType)}
          >
            {contentType}
          </ContentTypeLink>
        );
      })}
    </Stack>
  );
}
